import { initializeApp, getApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getAuth, setPersistence, browserLocalPersistence,
  GoogleAuthProvider, FacebookAuthProvider, EmailAuthProvider,
} from 'firebase/auth';
import type { Auth } from 'firebase/auth';
import {
  getFirestore, initializeFirestore, connectFirestoreEmulator,
  persistentLocalCache, persistentSingleTabManager,
} from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import type { FirebaseStorage } from 'firebase/storage';
import { getAnalytics } from 'firebase/analytics';
import type { Analytics } from 'firebase/analytics';
import { documentId } from 'firebase/firestore';
import { APP_CONFIG } from '../config';
import { isAndroid, isIos } from '../services/Device/device';

type AuthProviders = 'facebook' | 'google' | 'email';

class FirebaseProvider {
  private static instance: FirebaseProvider;

  private firebaseInstance: FirebaseApp;
  private firebaseAuth: Auth = null;
  private firebaseFirestore: Firestore = null;
  private firebaseStorage: FirebaseStorage = null;
  private firebaseAnalytics: Analytics = null;
  private persistenceEnabled: boolean = false;

  private constructor() {
    this.firebaseInstance = !getApps().length
      ? initializeApp(APP_CONFIG.firebase)
      : getApp();
  }

  static getInstance() {
    if (!FirebaseProvider.instance) {
      FirebaseProvider.instance = new FirebaseProvider();
    }
    return FirebaseProvider.instance;
  }

  public async getAuth(): Promise<Auth> {
    if (this.firebaseAuth === null) {
      this.firebaseAuth = getAuth(this.firebaseInstance);
      await setPersistence(this.firebaseAuth, browserLocalPersistence);
    }
    return this.firebaseAuth;
  }

  public async getFirestore(): Promise<Firestore> {
    if (this.firebaseFirestore === null) {
      const isNative = (await isIos()) || (await isAndroid());

      if (APP_CONFIG.environment === 'test') {
        this.firebaseFirestore = getFirestore(this.firebaseInstance);
        connectFirestoreEmulator(this.firebaseFirestore, 'localhost', 8080);
      } else if (isNative || this.persistenceEnabled) {
        this.firebaseFirestore = getFirestore(this.firebaseInstance);
      } else {
        this.persistenceEnabled = true;
        this.firebaseFirestore = initializeFirestore(this.firebaseInstance, {
          localCache: persistentLocalCache({
            tabManager: persistentSingleTabManager({}),
          }),
        });
      }
    }
    return this.firebaseFirestore;
  }

  public async getStorage(): Promise<FirebaseStorage> {
    if (this.firebaseStorage === null) {
      this.firebaseStorage = getStorage(this.firebaseInstance);
    }
    return this.firebaseStorage;
  }

  public async getAnalytics(): Promise<Analytics> {
    if (this.firebaseAnalytics === null) {
      this.firebaseAnalytics = getAnalytics(this.firebaseInstance);
    }
    return this.firebaseAnalytics;
  }

  public static getAuthProvider(provider: AuthProviders) {
    switch (provider) {
      case 'google':   return new GoogleAuthProvider();
      case 'facebook': return new FacebookAuthProvider();
      case 'email':    return new EmailAuthProvider();
    }
  }
}

function getDocumentField() {
  return documentId();
}

export { FirebaseProvider, getDocumentField };
